import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

const FLOW_STEPS = [
    { label: 'Send OTP' },
    { label: 'Verify OTP' },
];

const UdyamOtpFlowComponent = () => {
    const { sendServiceId, submitServiceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Step 0 — Send OTP
    const [regNumber, setRegNumber] = useState('');
    const [mobile, setMobile] = useState('');
    const [sendConsent, setSendConsent] = useState(false);
    const [sendErrors, setSendErrors] = useState({});
    const [sendResult, setSendResult] = useState(null);

    // Shared — client_id passed from step 0 to step 1
    const [clientId, setClientId] = useState('');

    // Step 1 — Verify OTP
    const [otp, setOtp] = useState('');
    const [submitConsent, setSubmitConsent] = useState(false);
    const [submitErrors, setSubmitErrors] = useState({});
    const [submitResult, setSubmitResult] = useState(null);

    useEffect(() => {
        if (!sendServiceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${sendServiceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [sendServiceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const msgs = { 500: 'Server error. Try again.', 429: 'Too many requests.', 404: 'No record found.' };
        let detail = msgs[s] || error.response?.data?.message || error.message || summary;
        if (s === 422) {
            const errs = error.response?.data?.errors;
            detail = errs ? Object.values(errs)[0]?.[0] : (error.response?.data?.message || 'Validation failed');
        }
        if (s === 402) {
            const c = error.response?.data || {};
            detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    // ── Step 0: Send OTP ────────────────────────────────────────────────────
    const validateSend = () => {
        const next = {};
        if (!regNumber.trim()) next.regNumber = 'Udyam registration number is required';
        else if (!/^UDYAM-[A-Z]{2}-\d{1,3}-\d{4,10}[A-Z]?$/i.test(regNumber.trim())) next.regNumber = 'Invalid format. Example: UDYAM-MH-02-0074027';
        if (!mobile.trim()) next.mobile = 'Mobile number is required';
        else if (!/^[6-9]\d{9}$/.test(mobile.trim())) next.mobile = 'Enter a valid 10-digit Indian mobile number';
        if (!sendConsent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSendOtp = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/udyam-otp/send-otp', {
                registration_number: regNumber.trim(),
                mobile_number: mobile.trim(),
                api_provider_service_id: parseInt(sendServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'OTP send failed');
            setSendResult(res.data);
            const d = res.data?.data?.data || res.data?.data || {};
            setClientId(d.client_id || d.clientId || '');
            toast.current?.show({ severity: 'success', summary: 'OTP sent', detail: 'Enter the OTP received on your mobile', life: 3000 });
        } catch (err) {
            showError(err, 'OTP send failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSendSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateSend();
        if (Object.keys(errs).length) {
            setSendErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setSendErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Registration:</span> <span className="text-900 font-medium">{regNumber}</span></div>
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{mobile}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm Udyam OTP send',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, send OTP',
            rejectLabel: 'Cancel',
            accept: doSendOtp,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    // ── Step 1: Verify OTP ──────────────────────────────────────────────────
    const validateSubmit = () => {
        const next = {};
        if (!otp.trim()) next.otp = 'OTP is required';
        else if (!/^\d{4,8}$/.test(otp.trim())) next.otp = 'Enter a valid OTP';
        if (!submitConsent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSubmitOtp = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/udyam-otp/submit-otp', {
                client_id: clientId,
                otp: otp.trim(),
                api_provider_service_id: parseInt(submitServiceId || sendServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'OTP verification failed');
            setSubmitResult(res.data);
            toast.current?.show({ severity: 'success', summary: 'Udyam verified', detail: 'OTP verified successfully', life: 2500 });
        } catch (err) {
            showError(err, 'OTP verification failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmitOtp = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateSubmit();
        if (Object.keys(errs).length) {
            setSubmitErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setSubmitErrors({});
        submittingRef.current = true;
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Client ID:</span> <span className="text-900 font-medium font-mono">{clientId}</span></div>
                    <div><span className="text-600">OTP:</span> <span className="text-900 font-medium">{otp}</span></div>
                </div>
            ),
            header: 'Confirm OTP verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: doSubmitOtp,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const resetFlow = () => {
        setStep(0);
        setRegNumber('');
        setMobile('');
        setSendConsent(false);
        setSendErrors({});
        setSendResult(null);
        setClientId('');
        setOtp('');
        setSubmitConsent(false);
        setSubmitErrors({});
        setSubmitResult(null);
        submittingRef.current = false;
    };

    // ── Renderers ────────────────────────────────────────────────────────────
    const renderStep0 = () => (
        <div>
            {!sendResult ? (
                <Card>
                    <form onSubmit={handleSendSubmit} noValidate>
                        <div className="grid">
                            <div className="field col-12 md:col-7">
                                <label htmlFor="udyamReg" className="block font-medium text-sm mb-2">
                                    Udyam Registration Number <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-briefcase" />
                                    <InputText
                                        id="udyamReg"
                                        value={regNumber}
                                        onChange={(e) => {
                                            setRegNumber((e.target.value || '').toUpperCase());
                                            if (sendErrors.regNumber) setSendErrors((p) => ({ ...p, regNumber: '' }));
                                        }}
                                        placeholder="e.g. UDYAM-MH-02-0074027"
                                        maxLength={25}
                                        spellCheck={false}
                                        disabled={loading}
                                        className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!sendErrors.regNumber })}
                                    />
                                </IconField>
                                {sendErrors.regNumber && (
                                    <small className="p-error block mt-1">
                                        <i className="pi pi-exclamation-circle mr-1" />{sendErrors.regNumber}
                                    </small>
                                )}
                            </div>
                            <div className="field col-12 md:col-5">
                                <label htmlFor="udyamMobile" className="block font-medium text-sm mb-2">
                                    Registered Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-phone" />
                                    <InputText
                                        id="udyamMobile"
                                        value={mobile}
                                        onChange={(e) => {
                                            const v = (e.target.value || '').replace(/\D/g, '');
                                            setMobile(v);
                                            if (sendErrors.mobile) setSendErrors((p) => ({ ...p, mobile: '' }));
                                        }}
                                        placeholder="9876543210"
                                        maxLength={10}
                                        inputMode="tel"
                                        disabled={loading}
                                        className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!sendErrors.mobile })}
                                    />
                                </IconField>
                                {sendErrors.mobile && (
                                    <small className="p-error block mt-1">
                                        <i className="pi pi-exclamation-circle mr-1" />{sendErrors.mobile}
                                    </small>
                                )}
                            </div>
                        </div>
                        <ConsentCheckbox
                            checked={sendConsent}
                            onChange={(e) => { setSendConsent(e.checked); if (sendErrors.consent) setSendErrors((p) => ({ ...p, consent: '' })); }}
                            error={sendErrors.consent}
                            reportType="udyam-otp-send"
                        />
                        <div className="flex justify-content-end mt-3">
                            <Button type="submit" label="Send OTP" icon="pi pi-send" iconPos="right" loading={loading} disabled={loading} size="small" />
                        </div>
                    </form>
                </Card>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={sendResult} title="OTP Sent" icon="pi-send" />
                    {clientId && (
                        <div className="mt-2 mb-3 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
                            <i className="pi pi-key text-primary text-sm" />
                            <span className="text-600 text-xs">Client ID (auto-passed to next step):</span>
                            <span className="font-mono text-sm text-900 font-medium">{clientId}</span>
                        </div>
                    )}
                    <div className="flex justify-content-between mt-3">
                        <Button label="Resend OTP" icon="pi pi-refresh" severity="secondary" outlined size="small"
                            onClick={() => { setSendResult(null); setClientId(''); setSendConsent(false); }} />
                        <Button label="Enter OTP" icon="pi pi-arrow-right" iconPos="right" size="small" onClick={() => setStep(1)} />
                    </div>
                </>
            )}
        </div>
    );

    const renderStep1 = () => (
        <div>
            {!submitResult ? (
                <Card>
                    {clientId && (
                        <div className="mb-3 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
                            <i className="pi pi-key text-primary text-sm" />
                            <span className="text-600 text-xs">Client ID:</span>
                            <span className="font-mono text-sm text-900 font-medium">{clientId}</span>
                        </div>
                    )}
                    <form onSubmit={handleSubmitOtp} noValidate>
                        <div className="grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="udyamOtp" className="block font-medium text-sm mb-2">
                                    OTP <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-lock" />
                                    <InputText
                                        id="udyamOtp"
                                        value={otp}
                                        onChange={(e) => {
                                            const v = (e.target.value || '').replace(/\D/g, '');
                                            setOtp(v);
                                            if (submitErrors.otp) setSubmitErrors((p) => ({ ...p, otp: '' }));
                                        }}
                                        placeholder="6-digit OTP"
                                        maxLength={8}
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        disabled={loading}
                                        className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!submitErrors.otp })}
                                    />
                                </IconField>
                                {submitErrors.otp && (
                                    <small className="p-error block mt-1">
                                        <i className="pi pi-exclamation-circle mr-1" />{submitErrors.otp}
                                    </small>
                                )}
                                <small className="text-500 block mt-1">OTP was sent to the registered mobile number</small>
                            </div>
                        </div>
                        <ConsentCheckbox
                            checked={submitConsent}
                            onChange={(e) => { setSubmitConsent(e.checked); if (submitErrors.consent) setSubmitErrors((p) => ({ ...p, consent: '' })); }}
                            error={submitErrors.consent}
                            reportType="udyam-otp-submit"
                        />
                        <div className="flex justify-content-between mt-3">
                            <Button type="button" label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} disabled={loading} />
                            <Button type="submit" label="Verify OTP" icon="pi pi-check" iconPos="right" loading={loading} disabled={loading} size="small" />
                        </div>
                    </form>
                </Card>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={submitResult} title="Udyam Verified" icon="pi-check-circle" />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} />
                        <Button label="Start Over" icon="pi pi-refresh" outlined size="small" onClick={resetFlow} />
                    </div>
                </>
            )}
        </div>
    );

    const stepRenderers = [renderStep0, renderStep1];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-send"
                        title="Udyam OTP Verification"
                        subtitle="Send OTP to the registered mobile number, then verify it to complete Udyam (MSME) verification"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default UdyamOtpFlowComponent;
